import React from 'react';

const TokenPositionCard = ({ 
  token, 
  onQuickSell, 
  onAddToBot, 
  onSetAlert 
}) => {
  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toFixed(2);
  };

  const formatPrice = (price) => {
    if (price === 0) return '$0.00';
    if (price < 0.000001) return `${price.toExponential(2)}`;
    if (price < 0.01) return `${price.toFixed(6)}`;
    if (price < 1) return `${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatPercentage = (percent) => {
    if (!percent || percent === 0) return '0.00%';
    const formatted = percent.toFixed(2);
    return `${percent >= 0 ? '+' : ''}${formatted}%`;
  };

  const getBotStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'paused': return '#f59e0b';
      case 'stopped': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getBotStatusIcon = (status) => {
    switch (status) {
      case 'active': return '🟢';
      case 'paused': return '🟡';
      case 'stopped': return '🔴';
      default: return '⚪';
    }
  };

  return (
    <div className="token-position-card">
      <div className="token-header">
        <div className="token-identity">
          {token.logoURI && (
            <img 
              src={token.logoURI} 
              alt={token.symbol}
              className="token-logo"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <div className="token-names">
            <span className="symbol">{token.symbol}</span>
            <span className="name">{token.name}</span>
            <span className="address">
              {token.address.slice(0, 6)}...{token.address.slice(-4)}
            </span>
          </div>
        </div>
        
        {token.botStatus && (
          <div 
            className={`bot-indicator ${token.botStatus}`}
            style={{ color: getBotStatusColor(token.botStatus) }}
          >
            {getBotStatusIcon(token.botStatus)} {token.botStatus}
          </div>
        )}
      </div>
      
      <div className="position-metrics">
        <div className="metric-row">
          <div className="metric">
            <span className="label">Balance</span>
            <span className="value">{parseFloat(token.balance).toFixed(6)}</span>
          </div>
          <div className="metric">
            <span className="label">USD Value</span>
            <span className="value">${(token.usdValue || token.currentValue || 0).toFixed(2)}</span>
          </div>
        </div>
        
        <div className="metric-row">
          <div className="metric">
            <span className="label">Avg Buy Price</span>
            <span className="value">{formatPrice(token.averageBuyPrice || 0)}</span>
          </div>
          <div className="metric">
            <span className="label">Current Price</span>
            <span className="value">{formatPrice(token.currentPrice || 0)}</span>
          </div>
        </div>
        
        <div className="metric-row">
          <div className="metric">
            <span className="label">Total Invested</span>
            <span className="value">${(token.totalInvested || 0).toFixed(2)}</span>
          </div>
          <div className="metric">
            <span className="label">P&L</span>
            <span className={`value ${(token.pnl || token.unrealizedPnLPercent || 0) >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(token.pnl || token.unrealizedPnLPercent || 0)}
            </span>
          </div>
        </div>
        
        {token.priceChange24h !== undefined && (
          <div className="metric-row">
            <div className="metric">
              <span className="label">24h Change</span>
              <span className={`value ${token.priceChange24h >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(token.priceChange24h)}
              </span>
            </div>
            <div className="metric">
              <span className="label">Volume 24h</span>
              <span className="value">${formatNumber(token.volume24h || 0)}</span>
            </div>
          </div>
        )}
      </div>
      
      <div className="position-actions">
        <div className="primary-actions">
          <button 
            className="quick-sell-btn" 
            onClick={onQuickSell}
            title="Sell this position"
          >
            Quick Sell
          </button>
          
          <button 
            className="add-to-bot-btn" 
            onClick={onAddToBot}
            title={token.botStatus ? 'Manage bot settings' : 'Add to automated trading bot'}
          >
            {token.botStatus ? 'Manage Bot' : 'Add to Bot'}
          </button>
        </div>
        
        <div className="secondary-actions">
          <button 
            className="set-alert-btn" 
            onClick={onSetAlert}
            title="Set price alerts for this token"
          >
            Set Alert
          </button>
          
          {token.botStatus && (
            <div className="bot-info">
              <span className="bot-strategy">
                Strategy: {token.botStrategy || 'Default'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenPositionCard;